import { useCallback, useRef, useState } from "react";
import { UploadCloud, X, Image as ImageIcon, Star } from "lucide-react";
import { toast } from "sonner";

const MAX_IMAGES = 5;
const MAX_ORIGINAL_MB = 8; // taille brute max acceptée avant compression
const TARGET_MAX_WIDTH = 1600;
const TARGET_QUALITY = 0.82;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

// Redimensionne + compresse en base64 (JPEG) via canvas
async function fileToCompressedDataUrl(file) {
  if (!ACCEPTED.includes(file.type)) {
    throw new Error("Format non supporté (JPG, PNG ou WEBP uniquement)");
  }
  if (file.size > MAX_ORIGINAL_MB * 1024 * 1024) {
    throw new Error(`Image trop lourde (max ${MAX_ORIGINAL_MB} Mo)`);
  }
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Lecture du fichier impossible"));
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("Image invalide"));
    i.src = dataUrl;
  });
  const scale = Math.min(1, TARGET_MAX_WIDTH / img.width);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", TARGET_QUALITY);
}

export default function ImageUploader({ value = [], onChange, max = MAX_IMAGES, testId = "image-uploader" }) {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const images = Array.isArray(value) ? value : [];

  const addFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = max - images.length;
    if (room <= 0) {
      toast.error(`Vous avez atteint la limite de ${max} images`);
      return;
    }
    const toProcess = files.slice(0, room);
    if (files.length > room) toast.info(`Seulement ${room} image(s) ajoutée(s) (limite ${max})`);
    setBusy(true);
    try {
      const results = [];
      for (const f of toProcess) {
        try {
          const d = await fileToCompressedDataUrl(f);
          results.push(d);
        } catch (err) {
          toast.error(`${f.name} : ${err?.message || "erreur inconnue"}`);
        }
      }
      if (results.length) onChange([...images, ...results]);
    } finally {
      setBusy(false);
    }
  }, [images, max, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };

  const makeCover = (idx) => {
    if (idx === 0) return;
    const next = [images[idx], ...images.filter((_, i) => i !== idx)];
    onChange(next);
  };

  return (
    <div data-testid={testId}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        data-testid={`${testId}-dropzone`}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
          dragOver ? "border-[#E05638] bg-orange-50" : "border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100"
        }`}
      >
        <UploadCloud size={28} className="mx-auto text-slate-400" />
        <div className="mt-2 text-sm font-semibold text-slate-700">
          Glissez-déposez vos images ici
        </div>
        <div className="text-xs text-slate-500">
          ou cliquez pour parcourir · JPG / PNG / WEBP · jusqu'à {max} images
        </div>
        {busy && <div className="mt-2 text-xs text-[#E05638] font-semibold">Traitement en cours…</div>}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          data-testid={`${testId}-input`}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {images.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid={`${testId}-previews`}>
          {images.map((src, idx) => (
            <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 bg-white">
              <img src={src} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
              {idx === 0 ? (
                <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white">
                  <Star size={10} className="fill-white"/> Couverture
                </span>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); makeCover(idx); }}
                  data-testid={`${testId}-set-cover-${idx}`}
                  className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/95 text-slate-700 hover:bg-white shadow"
                >
                  <Star size={10}/> Couverture
                </button>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeAt(idx); }}
                data-testid={`${testId}-remove-${idx}`}
                className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center rounded-full bg-white/95 hover:bg-white text-slate-700 shadow"
                aria-label="Supprimer l'image"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {Array.from({ length: Math.max(0, max - images.length) }).map((_, i) => (
            <button
              type="button"
              key={`ph-${i}`}
              onClick={() => inputRef.current?.click()}
              className="aspect-square rounded-xl border-2 border-dashed border-slate-200 grid place-items-center text-slate-300 hover:border-slate-400 hover:text-slate-500"
            >
              <ImageIcon size={22} />
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 text-xs text-slate-500">
        {images.length}/{max} image(s) · La première image est utilisée comme couverture.
      </div>
    </div>
  );
}
